/**
 * Reconstructs a partial tree from flat search results using parentId/rootAssetId
 * relationships already present on each record. No extra API calls needed.
 *
 * @param {Array} flatResults - Flat array of search results with id, parentId, rootAssetId, etc.
 * @param {string} grouping - "siteAsset" or "floc"
 * @returns {{ rootData: Array, childData: Object, expandedRows: Object }}
 */
const buildSearchTree = (flatResults, grouping) => {
	if (!flatResults || flatResults.length === 0) {
		return { rootData: [], childData: {}, expandedRows: {} };
	}

	if (grouping === "siteAsset") {
		return buildSiteAssetTree(flatResults);
	}

	return buildFlocTree(flatResults);
};

/**
 * Site Asset mode: group results by rootAssetId.
 * Records where id === rootAssetId become root rows.
 * All others nest under their root.
 */
const buildSiteAssetTree = (flatResults) => {
	const rootData = [];
	const childData = {};
	const expandedRows = {};

	// Group all results by rootAssetId
	const groups = new Map();
	for (const item of flatResults) {
		const key = item.rootAssetId || item.id;
		if (!groups.has(key)) groups.set(key, []);
		groups.get(key).push(item);
	}

	for (const [rootId, items] of groups) {
		// Find the actual root record (where id === rootAssetId)
		const rootRecord = items.find((r) => r.id === rootId);
		const children = items.filter((r) => r.id !== rootId);

		if (rootRecord) {
			rootData.push({
				...rootRecord,
				hasChildren: children.length > 0,
			});
		} else if (children.length > 0) {
			// Root not in search results — promote the lowest-level child
			const sorted = [...children].sort((a, b) => a.level - b.level);
			const promoted = sorted.shift();
			rootData.push({
				...promoted,
				hasChildren: sorted.length > 0,
			});
			// Remaining children nest under the promoted record
			if (sorted.length > 0) {
				childData[promoted.id] = sorted.map((c) => ({
					...c,
					hasChildren: false,
				}));
				expandedRows[promoted.id] = true;
			}
			continue;
		}

		if (children.length > 0) {
			childData[rootId] = children.map((c) => ({
				...c,
				hasChildren: false,
			}));
			expandedRows[rootId] = true;
		}
	}

	return { rootData, childData, expandedRows };
};

/**
 * FLOC mode: build adjacency tree from parentId relationships within the result set.
 * Records whose parentId isn't in the result set become top-level.
 * Supports multi-level nesting.
 */
const buildFlocTree = (flatResults) => {
	const childData = {};
	const expandedRows = {};

	// Build a set of all IDs in the results for O(1) lookup
	const idSet = new Set(flatResults.map((r) => r.id));

	// Partition into roots (no in-set parent) and children (parent is in results)
	const childrenByParent = new Map();
	const roots = [];

	for (const item of flatResults) {
		if (item.parentId && idSet.has(item.parentId)) {
			// This item's parent is also in the results — nest it
			if (!childrenByParent.has(item.parentId)) {
				childrenByParent.set(item.parentId, []);
			}
			childrenByParent.get(item.parentId).push(item);
		} else {
			// No parent in results — top-level
			roots.push(item);
		}
	}

	// Build childData and override hasChildren flags
	const setHasChildren = (items) => {
		return items.map((item) => {
			const children = childrenByParent.get(item.id);
			const has = children && children.length > 0;
			if (has) {
				childData[item.id] = setHasChildren(children);
				expandedRows[item.id] = true;
			}
			return { ...item, hasChildren: has };
		});
	};

	const rootData = setHasChildren(roots);

	return { rootData, childData, expandedRows };
};

export default buildSearchTree;
